import { Flex, ButtonV2, TextV2, InputV2, Box } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import React, { useMemo, useState, useEffect } from 'react';
import { isEqual } from 'lodash-es';
import type { FiltersPanelProps } from './FiltersPanel.types';
import { useTranslation } from '@shared/core';
import { useDebouncedValue } from '@shared/hooks/useDebouncedValue';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { debounce } from 'lodash-es';
import { FiltersPanelSkeleton, UserRatingFacet, StarRatingFacet, PriceRangeFacet, FacilitiesFacet, PropertyTypeFacet, BrandFacet, PrioritizeTagsFacet } from './components';
import { Close, Search } from '@withjoy/joykit/icons';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import { PRICE_RANGE } from '../../constants';
import { AccommodationCategory } from '@graphql/generated-base';
import type { GuestRating } from '@shared/types/Accommodations.types';
import { useListingsPanelTelemetry } from '../ListingsPanel/ListingsPanel.telemetry';
import { SortOptionsFacet } from '../ListingsPanel/components/SortOptionsFacet';
import { JoyTravelBenefitsFacet } from './components/JoyTravelBenefitsFacet';
import { ShowAvailableOnlyFacet } from './components/ShowAvailableOnlyFacet';
import { CompactMap } from './components/CompactMap';
import { PopularFiltersFacet } from './components/PopularFiltersFacet';
import { ReservationPolicyFacet } from './components/ReservationPolicyFacet';

const STAR_RATING_VALUES = [5, 4, 3, 2, 1];

function toggleSortedValue<T extends string | number>(source: readonly T[], newValue: T): readonly T[] {
  if (source.includes(newValue)) return source.filter(r => r !== newValue);
  const result = [...source, newValue];
  result.sort((a, b) => (a < b ? -1 : a > b ? 1 : 0));
  return result;
}

const Container: React.FC<{}> = ({ children }) => {
  return (
    <Flex
      flexDirection="column"
      padding={6}
      width={{ _: 'full', md: pxToRem(292) }}
      maxWidth={{ _: 'full', md: pxToRem(292) }}
      height="100%"
      overflow="auto"
      rowGap={6}
    >
      {children}
    </Flex>
  );
};

const FILTERS_PANEL_ACTIONS_Z_INDEX = 2;

const FiltersPanelInner = ({
  prices,
  onClose,
  onReset,
  filters,
  eventName,
  facilityCounts,
  brandCounts,
  onFiltersChange,
  isLoadingPrices,
  starRatingCounts,
  guestRatingCounts,
  propertyCategoryCounts,
  hidePriceRangeFacet,
  availablePrioritizeTags,
  selectedPrioritizeTags,
  onPrioritizeTagToggle,
  supportedFacilities,
  supportedBrands,
  sortOption,
  onSortChange,
  currencyCode,
  navigatorLanguage,
  dynamicPriceMax,
  mapData,
  propertyNameSearch,
  onPropertyNameSearchChange,
  hideEventNameAndNote,
  showAvailableOnlyTooltip,
  showJoyTravelBenefitsFilter,
  includeCouplesPicks,
  isPanelVisible,
  renderExtraFilter,
  showFundSavingsSort,
  accentColor,
}: FiltersPanelProps) => {
  const { tq } = useTranslation('accommodationsConsole');
  const isMobileOrTablet = useIsMobileOrTablet();
  const telemetry = useListingsPanelTelemetry();

  // When the stored range exactly matches the static default [MIN, MAX], the user hasn't
  // actively set a price filter, so the slider should reflect the dynamic maximum
  // computed from actual listing prices instead. We check both endpoints to avoid
  // overriding a user who explicitly typed 1000 into the max input while having a
  // non-default min.
  const resolveMaxForSlider = useEventCallback((range: readonly [number, number]): readonly [number, number] => {
    const isAtStaticDefault = range[0] === PRICE_RANGE.MIN && range[1] === PRICE_RANGE.MAX;
    return isAtStaticDefault ? [range[0], dynamicPriceMax] : range;
  });

  const [priceRange, setPriceRange] = useState<readonly [number, number]>(() => resolveMaxForSlider(filters.priceRange));
  const [debouncedPriceRange, setDebouncedPriceRange] = useState<readonly [number, number]>(() => resolveMaxForSlider(filters.priceRange));
  const [searchInput, setSearchInput] = useState(propertyNameSearch);
  const { setValue: setDebouncedSearchInput, value: debouncedSearchInput } = useDebouncedValue<string>(700);
  const onPropertyNameSearchChangeEvent = useEventCallback(onPropertyNameSearchChange);

  useEffect(() => {
    const resolved = resolveMaxForSlider(filters.priceRange);
    if (!isEqual(priceRange, resolved)) {
      setPriceRange(resolved);
      setDebouncedPriceRange(resolved);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.priceRange, resolveMaxForSlider, dynamicPriceMax]);

  useEffect(() => {
    if (searchInput !== propertyNameSearch) {
      setSearchInput(propertyNameSearch);
    }
    // Only sync when external propertyNameSearch changes, not on local input changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [propertyNameSearch]);

  useEffect(() => {
    setDebouncedSearchInput(searchInput);
    return () => setDebouncedSearchInput.cancel();
  }, [searchInput, setDebouncedSearchInput]);

  useEffect(() => {
    if (debouncedSearchInput !== undefined) {
      onPropertyNameSearchChangeEvent(debouncedSearchInput);
    }
  }, [debouncedSearchInput, onPropertyNameSearchChangeEvent]);

  const guestRatingsOptions = useMemo(
    () => [
      { rating: 9, text: tq`Wonderful: 9+` },
      { rating: 8, text: tq`Very Good: 8+` },
      { rating: 7, text: tq`Good: 7+` },
      { rating: 6, text: tq`Pleasant: 6+` },
    ],
    [tq],
  );

  const propertyTypeOptions = useMemo(
    () => [
      { label: tq`Hotel`, value: AccommodationCategory.Hotel },
      { label: tq`Vacation Rental`, value: AccommodationCategory.VacationRental },
      { label: tq`Other`, value: AccommodationCategory.Other },
    ],
    [tq],
  );

  const handleShowAvailableOnlyToggle = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const action = event.target.checked ? 'added' : 'removed';
    telemetry.showAvailableOnlyFilterChanged({ action });
    const newFilters = { ...filters, showAvailableOnly: event.target.checked };
    onFiltersChange(newFilters);
  });

  const handleJoyTravelBenefitsToggle = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const action = event.target.checked ? 'added' : 'removed';
    telemetry.joyTravelBenefitsFilterChanged({ action });
    const newFilters = { ...filters, cug: event.target.checked };
    onFiltersChange(newFilters);
  });

  const handleFreeCancellationToggle = useEventCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const action = event.target.checked ? 'added' : 'removed';
    telemetry.freeCancellationFilterChanged({ action });
    const newFilters = { ...filters, freeCancellation: event.target.checked };
    onFiltersChange(newFilters);
  });

  const handleFacilityToggle = useEventCallback((facility: string) => {
    const isCurrentlySelected = filters.facilities?.includes(facility) || false;
    const action = isCurrentlySelected ? 'removed' : 'added';
    telemetry.facilityFilterChanged({ facility, action });
    const newFilters = { ...filters, facilities: toggleSortedValue(filters.facilities || [], facility) };
    onFiltersChange(newFilters);
  });

  const handlePropertyCategoryToggle = useEventCallback((category: AccommodationCategory) => {
    const isCurrentlySelected = filters.propertyCategories?.includes(category) || false;
    const action = isCurrentlySelected ? 'removed' : 'added';
    telemetry.propertyCategoryFilterChanged({ propertyCategory: category, action });
    const newFilters = { ...filters, propertyCategories: toggleSortedValue([...(filters.propertyCategories || [])], category) };
    onFiltersChange(newFilters);
  });

  const handleBrandToggle = useEventCallback((brand: string) => {
    const isCurrentlySelected = filters.brands?.includes(brand) || false;
    const action = isCurrentlySelected ? 'removed' : 'added';
    telemetry.brandFilterChanged({ brand, action });
    const newFilters = { ...filters, brands: toggleSortedValue(filters.brands || [], brand) };
    onFiltersChange(newFilters);
  });

  const handleStarRatingToggle = useEventCallback((rating: number): void => {
    const isCurrentlySelected = filters.starRating?.includes(rating) || false;
    const action = isCurrentlySelected ? 'removed' : 'added';
    telemetry.starRatingFilterChanged({ rating, action });
    const newFilters = { ...filters, starRating: toggleSortedValue(filters.starRating || [], rating) };
    onFiltersChange(newFilters);
  });

  const handleUserRatingToggle = useEventCallback((rating: GuestRating): void => {
    const isCurrentlySelected = filters.userRating?.includes(rating) || false;
    const action = isCurrentlySelected ? 'removed' : 'added';
    telemetry.guestRatingFilterChanged({ rating, action });
    const newFilters = { ...filters, userRating: toggleSortedValue(filters.userRating || [], rating) };
    onFiltersChange(newFilters);
  });

  const handlePriceRangeChange = useEventCallback((value: number | readonly number[]) => {
    const newRange: readonly [number, number] = Array.isArray(value) ? [value[0], value[1]] : [value, value];
    setPriceRange(newRange);
  });

  // Stable debounced setter — created once, not recreated per effect run
  const debouncedSetPriceRange = useMemo(() => debounce((value: readonly [number, number]) => setDebouncedPriceRange(value), 1000), []);

  useEffect(() => {
    debouncedSetPriceRange(priceRange);
    return () => debouncedSetPriceRange.cancel();
  }, [priceRange, debouncedSetPriceRange]);

  const normalizePriceRange = useEventCallback((range: readonly [number, number]): readonly [number, number] => {
    const isAtExpandedDefault = range[0] === PRICE_RANGE.MIN && range[1] >= dynamicPriceMax;
    return isAtExpandedDefault ? [PRICE_RANGE.MIN, PRICE_RANGE.MAX] : range;
  });

  useEffect(() => {
    const normalizedPriceRange = normalizePriceRange(debouncedPriceRange);

    if (!isEqual(normalizedPriceRange, filters.priceRange)) {
      telemetry.priceRangeFilterChanged({ minPrice: normalizedPriceRange[0], maxPrice: normalizedPriceRange[1] });
      const newFilters = { ...filters, priceRange: normalizedPriceRange };
      onFiltersChange(newFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedPriceRange]);

  const handleResetWithTelemetry = useEventCallback(() => {
    telemetry.allFiltersReset();
    onReset?.();
  });

  return (
    <>
      <Container>
        {mapData && !mapData.shouldShowFullMap && (
          <CompactMap
            location={mapData.location}
            onShowOnMap={mapData.onShowOnMap}
          />
        )}
        <Flex justifyContent={'space-between'}>
          {isMobileOrTablet || hideEventNameAndNote ? (
            <TextV2 typographyVariant="hed4">{tq`Filters`}</TextV2>
          ) : (
            <Flex
              flexDirection="column"
              gap={2}
            >
              <TextV2
                fontFamily="Forum"
                fontSize={pxToRem(24)}
                fontWeight={400}
              >
                {eventName}
              </TextV2>
              <TextV2 typographyVariant="label2">{tq`Browse hotels with exclusive rates just for our guests.`}</TextV2>
            </Flex>
          )}

          {isMobileOrTablet && <Close onClick={onClose} />}
        </Flex>

        {/* Above every filter on purpose: this decides what the page is about, so it reads before
            the controls that refine it. It is the page's premise rather than a refinement of it,
            which is also why `onReset` below leaves it alone. */}
        {!!availablePrioritizeTags?.length && onPrioritizeTagToggle && (
          <PrioritizeTagsFacet
            availableTags={availablePrioritizeTags}
            selectedTags={selectedPrioritizeTags ?? []}
            onToggle={onPrioritizeTagToggle}
          />
        )}

        <Box>
          <TextV2
            typographyVariant="hed2"
            marginBottom={pxToRem(10)}
          >
            {tq`Search by property name`}
          </TextV2>
          <InputV2
            value={searchInput}
            startElement={<Search />}
            placeholder={tq`e.g., Marriott`}
            aria-label={tq`Search by property name`}
            onChange={e => setSearchInput(e.target.value)}
          />
        </Box>

        {!hidePriceRangeFacet && (
          <PriceRangeFacet
            title={tq`Price (per night)`}
            priceRange={priceRange}
            onPriceRangeChange={handlePriceRangeChange}
            isLoading={isLoadingPrices}
            prices={prices}
            currencyCode={currencyCode}
            navigatorLanguage={navigatorLanguage}
            dynamicPriceMax={dynamicPriceMax}
            activeColor={accentColor}
          />
        )}

        {renderExtraFilter?.({ filters, onFiltersChange })}

        <ShowAvailableOnlyFacet
          title={tq`Show available hotels first`}
          isChecked={filters.showAvailableOnly}
          onToggle={handleShowAvailableOnlyToggle}
          showTooltipOnMount={showAvailableOnlyTooltip}
          tooltipContent={tq`We prioritize showing available hotels, but you can choose to see them all.`}
          isPanelVisible={isPanelVisible}
        />

        {isMobileOrTablet && (
          <SortOptionsFacet
            selectedValue={sortOption}
            onChange={onSortChange}
            includeCouplesPicks={includeCouplesPicks}
            includeFundSavings={showFundSavingsSort}
            accentColor={accentColor}
          />
        )}

        {showJoyTravelBenefitsFilter && (
          <JoyTravelBenefitsFacet
            title={tq`Joy Travel Benefits`}
            isCUGChecked={filters.cug || false}
            onCUGToggle={handleJoyTravelBenefitsToggle}
          />
        )}

        <PopularFiltersFacet
          filters={filters}
          onFiltersChange={onFiltersChange}
        />

        <ReservationPolicyFacet
          title={tq`Reservation policy`}
          label={tq`Free cancellation`}
          isChecked={filters.freeCancellation}
          onToggle={handleFreeCancellationToggle}
        />

        {supportedBrands.length > 1 && (
          <BrandFacet
            title={tq`Brand`}
            brands={supportedBrands}
            selectedBrands={filters.brands || []}
            brandCounts={brandCounts}
            onBrandToggle={handleBrandToggle}
          />
        )}

        {(Object.values(propertyCategoryCounts).some(count => count > 0) || (filters.propertyCategories && filters.propertyCategories.length > 0)) && (
          <PropertyTypeFacet
            title={tq`Property Type`}
            propertyTypes={propertyTypeOptions}
            selectedCategories={filters.propertyCategories || []}
            propertyCategoryCounts={propertyCategoryCounts}
            onCategoryToggle={handlePropertyCategoryToggle}
          />
        )}

        <UserRatingFacet
          title={tq`Guest Rating`}
          guestRatingsOptions={guestRatingsOptions}
          selectedRatings={filters.userRating}
          guestRatingCounts={guestRatingCounts}
          onRatingToggle={handleUserRatingToggle}
        />

        <StarRatingFacet
          title={tq`Star Rating`}
          description={tq`Hotel quality—1 star is basic, 5 stars is luxe`}
          starRatings={STAR_RATING_VALUES}
          selectedRatings={filters.starRating}
          starRatingCounts={starRatingCounts}
          onRatingToggle={handleStarRatingToggle}
        />

        <FacilitiesFacet
          title={tq`Facilities`}
          facilities={supportedFacilities}
          selectedFacilities={filters.facilities || []}
          facilityCounts={facilityCounts}
          onFacilityToggle={handleFacilityToggle}
        />
      </Container>
      {isMobileOrTablet && (
        <Flex
          gap={4}
          marginTop={6}
          zIndex={FILTERS_PANEL_ACTIONS_Z_INDEX}
          width={'100%'}
          position="sticky"
          bottom={0}
          background="white"
          padding={4}
          borderTop="1px solid"
          borderTopColor="mono3"
        >
          <ButtonV2
            variant="ghost"
            intent="neutral"
            width="50%"
            onClick={handleResetWithTelemetry}
          >
            {tq`Reset All Filters`}
          </ButtonV2>
          <ButtonV2
            variant="solid"
            intent="neutral"
            shape="rounded"
            width="50%"
            onClick={() => {
              onClose?.();
            }}
          >
            {tq`Show Results`}
          </ButtonV2>
        </Flex>
      )}
    </>
  );
};

const FiltersPanel = Object.assign(React.memo(FiltersPanelInner), {
  Skeleton: ({ showJoyTravelBenefitsFilter }: { showJoyTravelBenefitsFilter: boolean }) => (
    <Container>
      <FiltersPanelSkeleton showJoyTravelBenefitsFilter={showJoyTravelBenefitsFilter} />
    </Container>
  ),
});

export { FiltersPanel };
